import { Fragment, ReactNode, useCallback, useState, MouseEvent } from 'react';
import { IconButton, Menu, MenuItem, MenuProps, SxProps } from '@mui/material';
import { Link } from 'react-router-dom';

import { ListItemText } from '@occc-app/design-system';
import { Divider } from '../Divider';
import { MoreVertIcon } from '../../icons';

export type TableRowActionsMenuItem = {
  label: string;
  onClick?: () => void;
  to?: string;
  dataTestId?: string;
  disabled?: boolean;
  icon?: ReactNode;
  sx?: SxProps;
};

interface TableRowActionsMenuProps {
  menuItems: TableRowActionsMenuItem[];
  disabled?: boolean;
  iconButtonSx?: SxProps;
  divided?: boolean;
}

export const TableRowActionsMenu = ({
  menuItems,
  disabled,
  iconButtonSx,
  divided,
}: TableRowActionsMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const additionalActionsHandler = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleOptionsMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleMenuItemClick = (event: MouseEvent<HTMLLIElement>, item: TableRowActionsMenuItem) => {
    event.stopPropagation();
    handleOptionsMenuClose();
    item.onClick?.();
  };

  const handleMenuClose: NonNullable<MenuProps['onClose']> = (event) => {
    if (event instanceof Event) {
      event.stopPropagation();
    }
    handleOptionsMenuClose();
  };

  if (!menuItems.length) {
    return null;
  }

  return (
    <>
      <IconButton
        onClick={additionalActionsHandler}
        role='menu'
        sx={{ padding: 0, ...iconButtonSx }}
        disabled={disabled}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu open={!!anchorEl} onClose={handleMenuClose} anchorEl={anchorEl}>
        {menuItems.map((item, index) => (
          <Fragment key={item.label}>
            {divided && index > 0 && <Divider sx={{ mx: 2 }} />}
            <MenuItem
              dense
              {...(item.to ? { component: Link, to: item.to, target: '_blank' } : {})}
              disabled={item.disabled}
              onClick={(event: MouseEvent<HTMLLIElement>) => handleMenuItemClick(event, item)}
              data-testid={item.dataTestId}
              sx={item.sx}
            >
              <ListItemText primary={item.label} sx={{ mr: 1 }} />
              {item.icon}
            </MenuItem>
          </Fragment>
        ))}
      </Menu>
    </>
  );
};
